import { createContext, useContext, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { setApiLanguage } from "../lib/api";

const LanguageContext = createContext();

const LOCALE_MAP = { en: "en-US", es: "es-MX", de: "de-DE" };

export function LanguageProvider({ children }) {
  const { t: i18nT, i18n } = useTranslation();
  const language = i18n.language?.substring(0, 2) || "en";
  const locale = LOCALE_MAP[language] || "en-US";

  // Sync language to API interceptor so ALL requests include it
  useEffect(() => { setApiLanguage(language); }, [language]);

  const setLanguage = useCallback((lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("app_language", lang);
    setApiLanguage(lang);
  }, [i18n]);

  // Key-based translation — delegates to i18next
  const t = useCallback((key) => {
    const result = i18nT(key);
    return result;
  }, [i18nT]);

  // Inline translation — ti(en, es, de?)
  const ti = useCallback((en, es, de) => {
    if (language === "es") return es || en;
    if (language === "de") return de || en;
    return en;
  }, [language]);

  // Get localized field from a DB document
  const getLocalized = useCallback((item, field) => {
    if (!item) return "";
    if (language === "es") {
      const esField = `${field}_es`;
      if (item[esField]) return item[esField];
    }
    if (language === "de") {
      const deField = `${field}_de`;
      if (item[deField]) return item[deField];
    }
    return item[field] || "";
  }, [language]);

  // Locale-aware formatters — available to every component via useLanguage()
  const fmt = useMemo(() => {
    const numFmt = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const intFmt = new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return {
      /** Format date: en→03/24/2026  es→24/03/2026  de→24.03.2026 */
      date: (v, style) => {
        if (!v) return "";
        try {
          const d = typeof v === "string" ? new Date(v) : v;
          if (isNaN(d.getTime())) return String(v);
          const opts = style === "long" ? { year: "numeric", month: "long", day: "numeric" }
            : style === "medium" ? { year: "numeric", month: "short", day: "numeric" }
            : { year: "numeric", month: "2-digit", day: "2-digit" };
          return d.toLocaleDateString(locale, opts);
        } catch { return String(v); }
      },
      /** Format number: en→1,234.56  de→1.234,56. Also accepts currency code as 2nd arg for convenience */
      num: (v, decimalsOrCurrency) => {
        if (v == null || isNaN(v)) return "0";
        if (typeof decimalsOrCurrency === "string" && decimalsOrCurrency.length === 3) {
          const code = decimalsOrCurrency.toUpperCase();
          try {
            const s = new Intl.NumberFormat(locale, { style: "currency", currency: code, currencyDisplay: "narrowSymbol", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
            return `${code} ${s}`;  // CURRENCY-FTM-01: ISO disclosed
          } catch { /* fall through */ }
        }
        return decimalsOrCurrency === 0 ? intFmt.format(v) : numFmt.format(v);
      },
      /** Format currency: en/USD→$1,234.56  de/EUR→1.234,56 €.
       *  CURRENCY-FTM-01: currency is REQUIRED. Missing/invalid → explicit
       *  non-monetary state (never implicit USD, never a naked amount).
       *  opts.legacyPending=true → deterministic legacy row scheduled for an
       *  authorized migration → "Currency migration pending" (not the generic
       *  authority-failure state). Never use for real authority failures. */
      money: (v, currency, opts) => {
        const code = (currency || "").trim().toUpperCase();
        if (!(code.length === 3 && /^[A-Z]{3}$/.test(code)))
          return (opts && opts.legacyPending) ? "Currency migration pending" : "Currency unavailable";
        if (v == null || isNaN(v)) v = 0;
        // CURRENCY-FTM-01: ISO code is ALWAYS disclosed (a symbol like $/MX$ is
        // ambiguous). Render "MXN $1,234.56" — ISO code + narrow symbol.
        try {
          const s = new Intl.NumberFormat(locale, { style: "currency", currency: code, currencyDisplay: "narrowSymbol", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
          return `${code} ${s}`;
        } catch { return `${code} ${numFmt.format(v)}`; }
      },
      /** Format currency short (no decimals). CURRENCY-FTM-01: currency REQUIRED + ISO disclosed. */
      moneyShort: (v, currency, opts) => {
        const code = (currency || "").trim().toUpperCase();
        if (!(code.length === 3 && /^[A-Z]{3}$/.test(code)))
          return (opts && opts.legacyPending) ? "Currency migration pending" : "Currency unavailable";
        if (v == null || isNaN(v)) v = 0;
        try {
          const s = new Intl.NumberFormat(locale, { style: "currency", currency: code, currencyDisplay: "narrowSymbol", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
          return `${code} ${s}`;
        } catch { return `${code} ${intFmt.format(v)}`; }
      },
      /** Format percent: 0.85 → "85%" */
      pct: (v) => {
        if (v == null || isNaN(v)) return "0%";
        return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 1 }).format(v);
      },
      locale,
    };
  }, [locale]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, ti, getLocalized, fmt }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
